import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { PriceDiffDisplay } from "components/common/plan/MixPriceDiff";
import { useOverrideMixMaterials } from "./useOverrideMixMaterials";
import { MixRead } from "src/store/api/generatedApi";

// Zero-width space: lets the material chain wrap after a colon instead of
// mid-name in narrow cards.
const RATIO_SEPARATOR = ":\u200b";

export const OverrideRecipeMix = ({ mix }: { mix: MixRead }) => {
  const { ratioMaterials, additions } = useOverrideMixMaterials(mix);
  const grabMaterials = additions.filter(
    (material) => material.unit === "grab"
  );
  return (
    <Stack sx={{ gap: 0.5, minWidth: "45%" }}>
      {ratioMaterials.length > 0 && (
        <Box>
          <Typography variant="body2Mono" sx={{ display: "block" }}>
            {ratioMaterials
              .map((material) => material.materialName)
              .join(RATIO_SEPARATOR)}
          </Typography>
          <Typography
            variant="body2Mono"
            sx={{
              display: "block",
              fontWeight: "fontWeightBold",
              color: "primary.main",
            }}
          >
            {ratioMaterials.map((material) => material.totalMass).join(":")}
          </Typography>
        </Box>
      )}
      {grabMaterials.map((material) => (
        <Typography
          key={material.materialName}
          variant="body2Mono"
          sx={{
            display: "block",
            opacity: material.totalMass === 0 ? 0.5 : 1,
          }}
        >
          {material.materialName}{" "}
          <Box
            component="span"
            sx={{ fontWeight: "fontWeightBold", color: "primary.main" }}
          >
            +{material.totalMass}
          </Box>
        </Typography>
      ))}
    </Stack>
  );
};

export const OverrideRecipeCard = ({
  title,
  subtitle,
  mix,
  costDeltaPerTonne,
  selected,
  onSelect,
}: {
  title: string;
  subtitle: string | null;
  mix: MixRead;
  costDeltaPerTonne: number | null;
  selected: boolean;
  onSelect: () => void;
}) => (
  <ButtonBase
    disableRipple
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    sx={{
      display: "flex",
      alignItems: "stretch",
      textAlign: "left",
      width: "100%",
      "&.Mui-focusVisible": {
        outline: "2px solid",
        outlineColor: "primary.main",
      },
      justifyContent: "space-between",
      gap: 2,
      border: 1,
      borderColor: selected ? "primary.main" : "grey.300",
      borderRadius: 1,
      p: 1.5,
      cursor: "pointer",
      bgcolor: selected ? "action.selected" : "transparent",
      "&:hover": { borderColor: selected ? "primary.main" : "grey.500" },
    }}
  >
    <Box>
      <Typography variant="body1" sx={{ fontWeight: "fontWeightBold" }}>
        {title}
      </Typography>
      {subtitle !== null && (
        <Typography
          variant="body2Mono"
          sx={{ color: "text.secondary", mt: 0.5 }}
        >
          {subtitle}
        </Typography>
      )}
      {costDeltaPerTonne !== null && (
        <Box sx={{ mt: 1 }}>
          <PriceDiffDisplay
            priceDiff={costDeltaPerTonne}
            unitType="specific_cost"
          />
        </Box>
      )}
    </Box>
    <OverrideRecipeMix mix={mix} />
  </ButtonBase>
);
